/* eslint-disable no-underscore-dangle */
/* eslint-disable no-use-before-define */
/**
 * Mock ScenarioPlanning payloads for UI/UX work while BE data is incomplete.
 * Enable with REACT_APP_TESTING_EXERCISING_MOCK=true.
 * Auto-fallback only when the API returns an empty scenarios list (or the request fails, unless MOCK=false).
 *
 * Media embeds a remote MP4 via HTML5 <video> for testing/dev (no local download).
 * Sample handout PDF is served from /public/mock/testing-exercising/.
 */

import { SAMPLE_CLIP_DURATION_SEC, alignLogToVideo } from './utils/incidentLog';
import { flattenVideoQuestions } from './utils/questions';

export {
  spreadPauseBeats,
  resolveVideoAtSeconds,
  alignLogToVideo,
  getIncidentBeats,
  resolveActiveIncidentLogEntry,
} from './utils/incidentLog';
export { flattenVideoQuestions } from './utils/questions';

const MOCK_HANDOUT_URL = `${process.env.PUBLIC_URL || ''}/mock/testing-exercising/sample-handout.pdf`;

/**
 * Default clip for FE testing when API/media URL is missing.
 * Prefer a direct MP4 (Azure blob/CDN + SAS), not a VideoPress page URL.
 * Override without code change:
 *   REACT_APP_TESTING_EXERCISING_VIDEO_URL=https://….blob.core.windows.net/…/master.mp4?<sas>
 */
export const DEFAULT_DEV_VIDEO_URL = process.env.REACT_APP_TESTING_EXERCISING_VIDEO_URL;

function mockMediaForStage(_stageIndex, _title, pauseAtSecondsOverride) {
  const startSeconds = 0;
  const pauseAtSeconds = Number.isFinite(pauseAtSecondsOverride)
    ? pauseAtSecondsOverride
    : SAMPLE_CLIP_DURATION_SEC;
  return {
    videoUrl: DEFAULT_DEV_VIDEO_URL,
    youtubeVideoId: null,
    videoHtml: null,
    thumbnail: null,
    startSeconds,
    pauseAtSeconds,
  };
}

/** Apply env/default MP4 when mock media is missing (live API leaves empty → player placeholder). */
export function withDefaultVideoMedia(video = {}) {
  if (video.videoUrl || video.videoHtml || video.youtubeVideoId) return video;
  const fallbackUrl = String(DEFAULT_DEV_VIDEO_URL || '').trim();
  if (!fallbackUrl) return video;
  return {
    ...video,
    videoUrl: fallbackUrl,
    youtubeVideoId: null,
    videoHtml: null,
  };
}

function mockHandoutPdf(fileName) {
  return { fileName, downloadUrl: MOCK_HANDOUT_URL };
}

export const MOCK_INDUSTRIES = [
  {
    id: 'food-retailing',
    name: 'Food Retailing',
    impactStatement:
      'Store systems, EPOS and supplier portals may be offline. Prioritise till fallback procedures, cold-chain monitoring and customer communication at branch level.',
    pdf: mockHandoutPdf('food-retailing-handout.pdf'),
  },
  {
    id: 'medical-practice',
    name: 'Medical Practice',
    impactStatement:
      'Patient booking, clinical records and telephony may be disrupted. Focus on safeguarding access to critical patient lists and diverting urgent care appropriately.',
    pdf: mockHandoutPdf('medical-practice-handout.pdf'),
  },
  {
    id: 'motor-dealership',
    name: 'Motor Vehicle Dealership',
    impactStatement:
      'CRM, parts ordering and workshop scheduling systems may be unavailable. Protect vehicle stock records and agree manual sales / service workflows.',
    pdf: mockHandoutPdf('motor-dealership-handout.pdf'),
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing',
    impactStatement:
      'Production control, MES and logistics feeds may be compromised. Isolate plant networks, protect safety systems and assess WIP / shipping commitments.',
    pdf: mockHandoutPdf('manufacturing-handout.pdf'),
  },
];

const VIDEO_1_QUESTIONS = [
  {
    questionId: 101,
    questionText: 'Who should be notified at this stage?',
    helpText: null,
    isRequired: true,
    displayOrder: 1,
  },
  {
    questionId: 102,
    questionText: 'Is this sufficient to activate the Business Continuity Plan or Incident Response Plan?',
    helpText: null,
    isRequired: true,
    displayOrder: 2,
  },
  {
    questionId: 103,
    questionText: 'Who has authority to declare an incident?',
    helpText: null,
    isRequired: true,
    displayOrder: 3,
  },
  {
    questionId: 104,
    questionText: 'What critical business functions may already be affected?',
    helpText: null,
    isRequired: true,
    displayOrder: 4,
  },
  {
    questionId: 105,
    questionText: 'What information do you need before making further decisions?',
    helpText: null,
    isRequired: true,
    displayOrder: 5,
  },
  {
    questionId: 106,
    questionText: 'What immediate actions should be taken while the cause remains unknown?',
    helpText: null,
    isRequired: true,
    displayOrder: 6,
  },
  {
    questionId: 107,
    questionText: 'How will the organisation communicate if email becomes unavailable?',
    helpText: null,
    isRequired: true,
    displayOrder: 7,
  },
];

export const MOCK_SCENARIO_ID = 9001;
export const MOCK_SCENARIO_IDS = {
  ransomware: 9001,
  siteFire: 9002,
  supplierFailure: 9003,
  powerOutage: 9004,
};
export const MOCK_SUBMISSION_ID = 90001;

export function mockSubmissionIdForScenario(scenarioId = MOCK_SCENARIO_ID) {
  return 90000 + Number(scenarioId);
}

export function isMockSubmissionId(submissionId) {
  const id = Number(submissionId);
  if (!Number.isFinite(id)) return false;
  if (id === MOCK_SUBMISSION_ID) return true;
  return Object.values(MOCK_SCENARIO_IDS).some((scenarioId) => mockSubmissionIdForScenario(scenarioId) === id);
}

function findMockScenario(scenarioId) {
  return (
    MOCK_SCENARIOS_RESPONSE.scenarios.find((item) => item.scenarioId === Number(scenarioId)) ||
    MOCK_SCENARIOS_RESPONSE.scenarios[0]
  );
}

function findMockScenarioBySubmissionId(submissionId) {
  const id = Number(submissionId);
  return (
    MOCK_SCENARIOS_RESPONSE.scenarios.find((item) => mockSubmissionIdForScenario(item.scenarioId) === id) ||
    (id === MOCK_SUBMISSION_ID ? findMockScenario(MOCK_SCENARIO_ID) : null)
  );
}

function q(questionId, questionText, displayOrder = 1) {
  return { questionId, questionText, helpText: null, isRequired: true, displayOrder };
}

function stageVideo({
  videoId,
  stageIndex,
  heading,
  description,
  clockLabel,
  message,
  statusTag,
  incidentLog,
  questions,
  facilitatorNote,
}) {
  const media = mockMediaForStage(stageIndex, heading);
  const explicitPause = (incidentLog || []).find((entry) => entry.isCurrent)?.videoAtSeconds;
  const aligned = alignLogToVideo(incidentLog, {
    startSeconds: media.startSeconds,
    pauseAtSeconds: Number.isFinite(explicitPause) ? explicitPause : media.pauseAtSeconds,
  });
  const { pauseAtSeconds, incidentLog: timedLog } = aligned;
  return {
    videoId,
    heading,
    description,
    videoUrl: '',
    ...mockMediaForStage(stageIndex, heading, pauseAtSeconds),
    displayOrder: stageIndex + 1,
    stageOverlay: { clockLabel, message, statusTag },
    // Section bookmarks only — questions belong on the video.
    incidentLog: timedLog,
    pauseAtSeconds,
    questions: questions || [],
    facilitatorNote: facilitatorNote ? { title: 'FACILITATOR NOTE', paragraphs: facilitatorNote } : undefined,
  };
}

function buildTimedVideo({
  videoId,
  stageIndex,
  heading,
  description,
  stageOverlay,
  incidentLog,
  questions,
  facilitatorNote,
  displayOrder,
}) {
  const media = mockMediaForStage(stageIndex, heading);
  const explicitPause = (incidentLog || []).find((entry) => entry.isCurrent)?.videoAtSeconds;
  const aligned = alignLogToVideo(incidentLog, {
    startSeconds: media.startSeconds,
    pauseAtSeconds: Number.isFinite(explicitPause) ? explicitPause : media.pauseAtSeconds,
  });
  return {
    videoId,
    heading,
    description,
    videoUrl: '',
    ...mockMediaForStage(stageIndex, heading, aligned.pauseAtSeconds),
    displayOrder,
    stageOverlay,
    // Section bookmarks only — questions belong on the video.
    incidentLog: aligned.incidentLog,
    pauseAtSeconds: aligned.pauseAtSeconds,
    questions: questions || [],
    facilitatorNote,
  };
}

/** Fill empty videoHtml/videoUrl from mock stage media (keeps API questions/copy). */
export function enrichScenariosWithMockMedia(payload) {
  if (!payload || !Array.isArray(payload.scenarios)) return payload;

  return {
    ...payload,
    industries:
      Array.isArray(payload.industries) && payload.industries.length
        ? payload.industries.map((industry) => ({
            ...industry,
            pdf:
              industry.pdf?.downloadUrl && industry.pdf.downloadUrl !== '#'
                ? industry.pdf
                : mockHandoutPdf(industry.pdf?.fileName || `${industry.id || 'industry'}-handout.pdf`),
          }))
        : MOCK_INDUSTRIES,
    scenarios: payload.scenarios.map((scenario) => ({
      ...scenario,
      pdf:
        scenario.pdf?.downloadUrl && scenario.pdf.downloadUrl !== '#'
          ? scenario.pdf
          : mockHandoutPdf(scenario.pdf?.fileName || 'scenario-handout.pdf'),
      videos: (scenario.videos || []).map((video, index) => {
        const media = mockMediaForStage(index, video.heading);
        const explicitPause = (video.incidentLog || []).find((entry) => entry.isCurrent)?.videoAtSeconds;
        const startSeconds = video.startSeconds || media.startSeconds;
        const { incidentLog: timedLog, pauseAtSeconds } = alignLogToVideo(video.incidentLog, {
          startSeconds,
          pauseAtSeconds:
            video.pauseAtSeconds || (Number.isFinite(explicitPause) ? explicitPause : media.pauseAtSeconds),
        });
        const nextLog = timedLog.length ? timedLog : video.incidentLog;

        // Prefer an explicit playable URL from the API; otherwise embed the mock MP4.
        if (video.videoUrl || video.videoHtml) {
          return {
            ...video,
            incidentLog: nextLog,
            pauseAtSeconds,
            startSeconds,
          };
        }

        return {
          ...video,
          ...mockMediaForStage(index, video.heading, pauseAtSeconds),
          // Clear YouTube so the HTML5 embed path is used when no URL was provided.
          youtubeVideoId: null,
          incidentLog: nextLog,
          pauseAtSeconds,
          startSeconds,
        };
      }),
    })),
  };
}

export const MOCK_SCENARIOS_RESPONSE = {
  industryId: 1,
  industryName: 'Food Retailing',
  industries: MOCK_INDUSTRIES,
  scenarios: [
    {
      scenarioId: MOCK_SCENARIO_ID,
      heading: 'Ransomware Scenario',
      description:
        'A facilitated walkthrough of an unfolding ransomware incident — from first symptoms through escalation and initial response.',
      displayOrder: 1,
      pdf: MOCK_INDUSTRIES[0].pdf,
      videos: [
        buildTimedVideo({
          videoId: 1,
          stageIndex: 0,
          heading: 'Initial Detection',
          description: 'Press Play to start. Use the incident log to jump between sections of this video.',
          displayOrder: 1,
          stageOverlay: {
            clockLabel: 'INCIDENT CLOCK: NOW',
            message: 'Remote workers are also experiencing connection problems.',
            statusTag: 'AWAITING YOUR CALL',
          },
          // Section bookmarks — offsets auto-spread across the ~2 min sample (narrative times are exercise labels).
          incidentLog: [
            {
              id: 'v1-1',
              time: '08:15',
              message: 'Email and shared drives unreachable. Helpdesk tickets rising quickly.',
              tag: 'CAUSE UNKNOWN',
            },
            {
              id: 'v1-2',
              time: '08:25',
              message: "Reports spreading. Yesterday's files won't open — encryption suspected.",
              tag: 'SPREADING',
            },
            {
              id: 'v1-3',
              time: '08:35',
              message: 'Multiple servers unresponsive. Hardware, software or cyber cause not yet confirmed.',
              tag: 'ESCALATED TO MGMT',
            },
            {
              id: 'v1-4',
              time: 'NOW',
              message: 'Website and phones still operational. No customer impact reported — yet.',
              tag: 'AWAITING YOUR CALL',
              isCurrent: true,
            },
          ],
          questions: VIDEO_1_QUESTIONS,
          facilitatorNote: {
            title: 'FACILITATOR NOTE',
            paragraphs: [
              'Pause the exercise. Allow participants 10–15 minutes to discuss the situation and document their proposed response before proceeding to Video 2.',
              'Participants should focus on incident recognition, escalation, communication and initial response activities.',
            ],
          },
        }),
        buildTimedVideo({
          videoId: 2,
          stageIndex: 1,
          heading: 'Escalation & Containment',
          description: 'The incident widens. Discuss containment options and who owns the next decisions.',
          displayOrder: 2,
          stageOverlay: {
            clockLabel: 'INCIDENT CLOCK: +45 MIN',
            message: 'Core file servers offline. Ransom note discovered on a shared drive.',
            statusTag: 'CONTAINMENT',
          },
          incidentLog: [
            {
              id: 'v2-1',
              time: '09:00',
              message: 'Ransom note found. IT isolates affected VLANs.',
              tag: 'ISOLATION',
            },
            {
              id: 'v2-2',
              time: '09:20',
              message: 'CMT convened. Legal and cyber insurer notified.',
              tag: 'CMT ACTIVE',
            },
            {
              id: 'v2-3',
              time: 'NOW',
              message: 'Awaiting forensic triage. Customer-facing site still up.',
              tag: 'AWAITING DECISION',
              isCurrent: true,
            },
          ],
          questions: [
            {
              questionId: 201,
              questionText: 'Which systems should be isolated first, and who can authorise that?',
              isRequired: true,
              displayOrder: 1,
            },
            {
              questionId: 202,
              questionText: 'What is your external notification plan (insurer, regulator, customers)?',
              isRequired: true,
              displayOrder: 2,
            },
            {
              questionId: 203,
              questionText: 'How will you protect evidence for forensics while restoring service?',
              isRequired: true,
              displayOrder: 3,
            },
          ],
          facilitatorNote: {
            title: 'FACILITATOR NOTE',
            paragraphs: ['Encourage the team to separate technical containment from business communication decisions.'],
          },
        }),
        buildTimedVideo({
          videoId: 3,
          stageIndex: 2,
          heading: 'Business Continuity Activation',
          description: 'Decide whether to activate the BCP and which workarounds apply.',
          displayOrder: 3,
          stageOverlay: {
            clockLabel: 'INCIDENT CLOCK: +2 HRS',
            message: 'Critical processes need manual workarounds while recovery is assessed.',
            statusTag: 'BCP REVIEW',
          },
          incidentLog: [
            {
              id: 'v3-1',
              time: '10:15',
              message: 'Priority products and customers identified.',
              tag: 'PRIORITISED',
            },
            {
              id: 'v3-2',
              time: 'NOW',
              message: 'Decision point: activate BCP / IRP and stand up recovery teams.',
              tag: 'ACTIVATION',
              isCurrent: true,
            },
          ],
          questions: [
            {
              questionId: 301,
              questionText: 'Which recovery strategies from your plan apply now?',
              isRequired: true,
              displayOrder: 1,
            },
            {
              questionId: 302,
              questionText: 'What is the RTO for the most critical function affected?',
              isRequired: true,
              displayOrder: 2,
            },
          ],
          facilitatorNote: {
            title: 'FACILITATOR NOTE',
            paragraphs: ['Link answers back to the Recovery Strategy and BIA RTO values already in ContinuityCoach.'],
          },
        }),
        buildTimedVideo({
          videoId: 4,
          stageIndex: 3,
          heading: 'Stakeholder Communication',
          description: 'Craft internal and external messaging while facts are still incomplete.',
          displayOrder: 4,
          stageOverlay: {
            clockLabel: 'INCIDENT CLOCK: +4 HRS',
            message: 'Media and key customers are asking what is happening.',
            statusTag: 'COMMS',
          },
          incidentLog: [
            {
              id: 'v4-1',
              time: '12:00',
              message: 'Holding statement drafted for staff.',
              tag: 'INTERNAL',
            },
            {
              id: 'v4-2',
              time: 'NOW',
              message: 'Approve customer / partner messaging with Legal.',
              tag: 'EXTERNAL',
              isCurrent: true,
            },
          ],
          questions: [
            {
              questionId: 401,
              questionText: 'Who approves external communications?',
              isRequired: true,
              displayOrder: 1,
            },
            {
              questionId: 402,
              questionText: 'What channels will you use if email and intranet are down?',
              isRequired: true,
              displayOrder: 2,
            },
          ],
          facilitatorNote: {
            title: 'FACILITATOR NOTE',
            paragraphs: ['Keep messages factual. Avoid speculation about cause or blame.'],
          },
        }),
        buildTimedVideo({
          videoId: 5,
          stageIndex: 4,
          heading: 'Recovery & Lessons',
          description: 'Close the exercise with recovery priorities and improvement actions.',
          displayOrder: 5,
          stageOverlay: {
            clockLabel: 'INCIDENT CLOCK: +1 DAY',
            message: 'Restore priority services and capture lessons learned.',
            statusTag: 'RECOVERY',
          },
          incidentLog: [
            {
              id: 'v5-1',
              time: 'D+1',
              message: 'Priority systems restored from backups.',
              tag: 'RESTORE',
            },
            {
              id: 'v5-2',
              time: 'NOW',
              message: 'Hot debrief — findings and actions for the plan.',
              tag: 'DEBRIEF',
              isCurrent: true,
            },
          ],
          questions: [
            {
              questionId: 501,
              questionText: 'What plan updates should be logged from this exercise?',
              isRequired: true,
              displayOrder: 1,
            },
            {
              questionId: 502,
              questionText: 'Which findings need owners and due dates?',
              isRequired: true,
              displayOrder: 2,
            },
          ],
          facilitatorNote: {
            title: 'FACILITATOR NOTE',
            paragraphs: ['Capture actions that can feed the plan document and next validation cycle.'],
          },
        }),
      ],
    },
    {
      scenarioId: MOCK_SCENARIO_IDS.siteFire,
      heading: 'Site Fire & Evacuation',
      description:
        'A fire forces evacuation of the primary site. Walk through life-safety first response, alternate site activation and stakeholder updates.',
      displayOrder: 2,
      pdf: MOCK_INDUSTRIES[1].pdf,
      videos: [
        stageVideo({
          videoId: 2101,
          stageIndex: 0,
          heading: 'Alarm & Evacuation',
          description: 'Fire alarm activated. Focus on people safety before business recovery.',
          clockLabel: 'INCIDENT CLOCK: NOW',
          message: 'Smoke reported in the plant room. Full site evacuation underway.',
          statusTag: 'EVACUATION',
          incidentLog: [
            {
              id: 'sf-1',
              time: '14:05',
              message: 'Fire alarm and evacuation initiated. Assembly points filling.',
              tag: 'ALARM',
            },
            {
              id: 'sf-2',
              time: 'NOW',
              message: 'Emergency services on site. Building re-entry not authorised.',
              tag: 'AWAITING YOUR CALL',
              isCurrent: true,
            },
          ],
          questions: [
            q(2101, 'Who accounts for staff, contractors and visitors at the assembly point?', 1),
            q(2102, 'Who liaises with emergency services and authorises re-entry?', 2),
            q(2103, 'What must be secured or shut down before leaving the building?', 3),
          ],
          facilitatorNote: [
            'Keep the discussion on life safety first. Business recovery comes after people are accounted for.',
          ],
        }),
        stageVideo({
          videoId: 2102,
          stageIndex: 1,
          heading: 'Alternate Site & Continuity',
          description: 'Decide how critical work continues while the primary site is unavailable.',
          clockLabel: 'INCIDENT CLOCK: +3 HRS',
          message: 'Building closed for investigation. Staff need direction for tomorrow.',
          statusTag: 'WORKAROUND',
          incidentLog: [
            {
              id: 'sf-3',
              time: '17:00',
              message: 'Landlord confirms site closed for at least 48 hours.',
              tag: 'SITE LOSS',
            },
            {
              id: 'sf-4',
              time: 'NOW',
              message: 'CMT deciding alternate working arrangements and priority functions.',
              tag: 'BCP REVIEW',
              isCurrent: true,
            },
          ],
          questions: [
            q(2201, 'Which alternate site or work-from-home arrangements apply?', 1),
            q(2202, 'Which critical functions must resume within the first 24 hours?', 2),
            q(2203, 'How will you access plan documents and contact lists off-site?', 3),
          ],
          facilitatorNote: ['Cross-check answers against your Relocation / Alternate Site strategy.'],
        }),
        stageVideo({
          videoId: 2103,
          stageIndex: 2,
          heading: 'Comms & Return-to-Site',
          description: 'Coordinate staff, customers and insurers while planning return.',
          clockLabel: 'INCIDENT CLOCK: +1 DAY',
          message: 'Investigation ongoing. Customers asking about service delays.',
          statusTag: 'COMMS',
          incidentLog: [
            {
              id: 'sf-5',
              time: 'D+1',
              message: 'Insurer and landlord updates received. Partial re-entry possible next week.',
              tag: 'UPDATE',
            },
            {
              id: 'sf-6',
              time: 'NOW',
              message: 'Approve staff and customer communications; schedule return plan.',
              tag: 'DEBRIEF',
              isCurrent: true,
            },
          ],
          questions: [
            q(2301, 'What is the holding message for customers and key suppliers?', 1),
            q(2302, 'What evidence and actions should be logged for insurance?', 2),
          ],
          facilitatorNote: ['Capture return-to-site criteria and owners before closing the exercise.'],
        }),
      ],
    },
    {
      scenarioId: MOCK_SCENARIO_IDS.supplierFailure,
      heading: 'Critical Supplier Failure',
      description:
        'A key upstream supplier stops deliveries with little notice. Practice prioritisation, alternate sourcing and customer commitments.',
      displayOrder: 3,
      pdf: MOCK_INDUSTRIES[2].pdf,
      videos: [
        stageVideo({
          videoId: 3101,
          stageIndex: 0,
          heading: 'Supply Disruption Detected',
          description: 'Orders from a critical supplier will not arrive. Assess immediate impact.',
          clockLabel: 'INCIDENT CLOCK: NOW',
          message: 'Supplier notifies: no outbound shipments for at least 5 days.',
          statusTag: 'SUPPLY RISK',
          incidentLog: [
            {
              id: 'su-1',
              time: '09:30',
              message: 'Procurement receives stop-shipment notice from primary supplier.',
              tag: 'ALERT',
            },
            {
              id: 'su-2',
              time: 'NOW',
              message: 'Stock cover for priority SKUs is under 48 hours.',
              tag: 'AWAITING YOUR CALL',
              isCurrent: true,
            },
          ],
          questions: [
            q(3101, 'Which products or services are affected first?', 1),
            q(3102, 'Who owns supplier relationship escalation and contract remedies?', 2),
            q(3103, 'What stock or buffer can buy time while alternatives are found?', 3),
          ],
          facilitatorNote: ['Link the discussion to single points of failure identified in your BIA.'],
        }),
        stageVideo({
          videoId: 3102,
          stageIndex: 1,
          heading: 'Alternate Sourcing',
          description: 'Evaluate secondary suppliers, substitutions and temporary process changes.',
          clockLabel: 'INCIDENT CLOCK: +6 HRS',
          message: 'Secondary quotes arriving. Quality and lead-time trade-offs unclear.',
          statusTag: 'SOURCING',
          incidentLog: [
            {
              id: 'su-3',
              time: '15:00',
              message: 'Two alternate suppliers contacted; one can partially cover in 72 hours.',
              tag: 'OPTIONS',
            },
            {
              id: 'su-4',
              time: 'NOW',
              message: 'Decision needed on substitutions and customer allocation.',
              tag: 'AWAITING DECISION',
              isCurrent: true,
            },
          ],
          questions: [
            q(3201, 'What criteria decide which customers get limited stock first?', 1),
            q(3202, 'Who can approve substitute products or temporary process changes?', 2),
          ],
          facilitatorNote: ['Push for clear allocation rules — avoid ad-hoc favouritism under pressure.'],
        }),
        stageVideo({
          videoId: 3103,
          stageIndex: 2,
          heading: 'Customer Impact & Lessons',
          description: 'Communicate delays and capture supply-chain resilience actions.',
          clockLabel: 'INCIDENT CLOCK: +2 DAYS',
          message: 'Partial cover secured. Key accounts need proactive updates.',
          statusTag: 'RECOVERY',
          incidentLog: [
            {
              id: 'su-5',
              time: 'D+2',
              message: 'Alternate supply online for 60% of priority demand.',
              tag: 'PARTIAL',
            },
            {
              id: 'su-6',
              time: 'NOW',
              message: 'Debrief: dual-sourcing and buffer stock actions.',
              tag: 'DEBRIEF',
              isCurrent: true,
            },
          ],
          questions: [
            q(3301, 'How will you communicate revised delivery commitments?', 1),
            q(3302, 'What plan or supplier changes should this exercise trigger?', 2),
          ],
        }),
      ],
    },
    {
      scenarioId: MOCK_SCENARIO_IDS.powerOutage,
      heading: 'Prolonged Power Outage',
      description:
        'A regional outage knocks out mains power beyond UPS runtime. Practice triage, generator decisions and service prioritisation.',
      displayOrder: 4,
      pdf: MOCK_INDUSTRIES[3].pdf,
      videos: [
        stageVideo({
          videoId: 4101,
          stageIndex: 0,
          heading: 'Mains Failure',
          description: 'Power is out. UPS is covering critical kit — for now.',
          clockLabel: 'INCIDENT CLOCK: NOW',
          message: 'Site-wide power loss. Utility ETA unknown; UPS at 40 minutes.',
          statusTag: 'UTILITIES',
          incidentLog: [
            {
              id: 'pw-1',
              time: '11:10',
              message: 'Mains failure confirmed across the campus.',
              tag: 'OUTAGE',
            },
            {
              id: 'pw-2',
              time: 'NOW',
              message: 'UPS covering servers and phones. HVAC and production offline.',
              tag: 'AWAITING YOUR CALL',
              isCurrent: true,
            },
          ],
          questions: [
            q(4101, 'Which loads stay on UPS / generator, and who decides load shedding?', 1),
            q(4102, 'What staff welfare and H&S issues arise without HVAC or lighting?', 2),
            q(4103, 'When do you declare an incident and stand up the CMT?', 3),
          ],
          facilitatorNote: ['Separate life-safety and equipment protection from IT recovery decisions.'],
        }),
        stageVideo({
          videoId: 4102,
          stageIndex: 1,
          heading: 'Generator & Priority Services',
          description: 'Fuel, generator capacity and which services run first.',
          clockLabel: 'INCIDENT CLOCK: +2 HRS',
          message: 'Generator started. Fuel for 8 hours. Utility still estimating restoration.',
          statusTag: 'STABILISING',
          incidentLog: [
            {
              id: 'pw-3',
              time: '13:00',
              message: 'Generator online. Non-critical circuits remain dark.',
              tag: 'GENERATOR',
            },
            {
              id: 'pw-4',
              time: 'NOW',
              message: 'Decide which customer-facing and production services restart.',
              tag: 'PRIORITISE',
              isCurrent: true,
            },
          ],
          questions: [
            q(4201, 'Which services restart first given limited generator capacity?', 1),
            q(4202, 'What is the fuel resupply plan if the outage lasts overnight?', 2),
          ],
        }),
        stageVideo({
          videoId: 4103,
          stageIndex: 2,
          heading: 'Extended Outage & Recovery',
          description: 'Plan for multi-day outage, remote working and catch-up after power returns.',
          clockLabel: 'INCIDENT CLOCK: +1 DAY',
          message: 'Utility now advising 36–48 hour restoration window.',
          statusTag: 'EXTENDED',
          incidentLog: [
            {
              id: 'pw-5',
              time: 'D+1',
              message: 'Partial remote working enabled. Cold-chain / production still limited.',
              tag: 'WORKAROUND',
            },
            {
              id: 'pw-6',
              time: 'NOW',
              message: 'Document recovery sequence for when mains returns.',
              tag: 'DEBRIEF',
              isCurrent: true,
            },
          ],
          questions: [
            q(4301, 'How will you sequence restart to avoid surge / equipment damage?', 1),
            q(4302, 'What validation actions should feed the utilities strategy?', 2),
          ],
          facilitatorNote: ['Close with owners for generator testing, fuel contracts and UPS maintenance.'],
        }),
      ],
    },
  ],
};

export function buildMockSubmissionDetail({
  planId = 0,
  scenarioId = MOCK_SCENARIO_ID,
  submissionId = MOCK_SUBMISSION_ID,
  participant = 'Crisis Management Team',
  status = 'Draft',
} = {}) {
  const scenario =
    findMockScenarioBySubmissionId(submissionId) ||
    findMockScenario(scenarioId) ||
    MOCK_SCENARIOS_RESPONSE.scenarios[0];
  const resolvedSubmissionId = Number(submissionId) || mockSubmissionIdForScenario(scenario.scenarioId);

  return {
    submissionId: resolvedSubmissionId,
    planId,
    scenarioId: scenario.scenarioId,
    scenarioHeading: scenario.heading,
    startingText: null,
    conclusionText:
      'This concludes the exercise.\r\nParticipants should now undertake a facilitated debrief and document any observations, lessons learned and improvement opportunities.',
    questionnaireDate: new Date().toISOString(),
    participant,
    status,
    submittedDate: null,
    cancelledDate: null,
    createdDate: new Date().toISOString(),
    modifiedDate: new Date().toISOString(),
    videos: (scenario.videos || []).map((video) => ({
      videoId: video.videoId,
      heading: video.heading,
      videoTiming: '2-3 Minutes',
      questions: flattenVideoQuestions(video).map((question) => ({
        questionId: question.questionId,
        questionText: question.questionText,
        isRequired: Boolean(question.isRequired),
        answerText: null,
      })),
    })),
  };
}

export function buildMockSubmissionsList(planId, scenarioId) {
  const scenario = findMockScenario(scenarioId);

  return {
    planId,
    scenarioId: scenario.scenarioId,
    scenarioHeading: scenario.heading,
    submissions: [],
  };
}

/** Explicit mock flag, or when the scenarios list is empty (nothing usable from API). */
export function shouldUseTestingExercisingMock(apiPayload) {
  const flag = String(process.env.REACT_APP_TESTING_EXERCISING_MOCK || '').toLowerCase();
  if (flag === 'true' || flag === '1') return true;
  if (flag === 'false' || flag === '0') return false;

  const scenarios = apiPayload?.scenarios;
  // Prefer live API whenever it returns any scenarios (including summary-only shells).
  return !Array.isArray(scenarios) || scenarios.length === 0;
}

/** Normalize submissions list — flat `submissions[]` or current/previous split. */
export function normalizeSubmissionsPayload(payload = {}) {
  if (Array.isArray(payload.submissions)) {
    return {
      ...payload,
      submissions: payload.submissions,
    };
  }

  const current = Array.isArray(payload.currentSubmissions) ? payload.currentSubmissions : [];
  const previous = Array.isArray(payload.previousSubmissions) ? payload.previousSubmissions : [];
  return {
    ...payload,
    submissions: [...current, ...previous],
  };
}

export function delay(ms = 350) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
